import { ReactNode, SyntheticEvent, useCallback, useId, useSyncExternalStore } from "react";
import { twMerge } from "tailwind-merge";
import Icon from "~/components/shared/Icon";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardPortal,
} from "~/components/shared/HoverCard";
import { Type } from "~/lib/pipeline/Step/Step";

// Shared state so moving between hovercards skips the open delay
// and only one hovercard is open at a time
let anyOpen = false;
let activeId: string | null = null;
let graceTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function getSnapshot() {
  return { warmed: anyOpen, activeId };
}

let cachedSnapshot = getSnapshot();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function notify() {
  cachedSnapshot = getSnapshot();
  listeners.forEach((cb) => cb());
}

function openHovercard(id: string) {
  if (graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
  anyOpen = true;
  activeId = id;
  notify();
}

function closeHovercard(id: string) {
  if (activeId !== id) {
    return;
  }
  activeId = null;
  graceTimer = setTimeout(() => {
    anyOpen = false;
    notify();
  }, 300);
  notify();
}

function stopPropagation(event: SyntheticEvent) {
  event.stopPropagation();
}

const STEP_TYPE_ICON: Record<Type, string> = {
  [Type.Command]: "stepTypeCommand",
  [Type.Trigger]: "stepTypeTrigger",
  [Type.Group]: "stepTypeGroup",
  [Type.Input]: "stepTypeInput",
  [Type.Block]: "stepTypeBlock",
  [Type.Wait]: "stepTypeWait",
};

const STEP_TYPE_LABEL: Partial<Record<Type, string>> = {
  [Type.Command]: "Command",
  [Type.Trigger]: "Trigger",
  [Type.Input]: "Input",
  [Type.Block]: "Block",
  [Type.Wait]: "Wait",
};

function StepTypeBadge({ stepType, script }: { stepType: Type; script?: ReactNode }) {
  const iconName = STEP_TYPE_ICON[stepType];
  const fill = stepType === Type.Command || stepType === Type.Trigger ? "none" : "currentColor";
  const id = useId();
  const snapshot = useSyncExternalStore(subscribe, () => cachedSnapshot);
  const isOpen = snapshot.activeId === id;
  const label = STEP_TYPE_LABEL[stepType];

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openHovercard(id);
      } else {
        closeHovercard(id);
      }
    },
    [id],
  );

  if (label) {
    return (
      <HoverCard
        open={isOpen}
        openDelay={snapshot.warmed ? 0 : 500}
        closeDelay={100}
        onOpenChange={handleOpenChange}
      >
        <HoverCardTrigger asChild={true}>
          <span className="text-charcoal-300 hover:text-charcoal-800 -mr-0.5 flex shrink-0 items-center p-1 transition-colors">
            <Icon icon={iconName} className="size-4" style={{ fill }} />
          </span>
        </HoverCardTrigger>
        <HoverCardPortal>
          <HoverCardContent
            className="z-50"
            hideArrow={true}
            size="max-lg"
            side="right"
            onClick={stopPropagation}
            onPointerDown={stopPropagation}
          >
            <p className="text-charcoal-600 m-0 text-xs font-medium">{label}</p>
            {script && (
              <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-xs">{script}</pre>
            )}
          </HoverCardContent>
        </HoverCardPortal>
      </HoverCard>
    );
  }

  return (
    <Icon icon={iconName} className="text-charcoal-300 ml-1 size-4 shrink-0" style={{ fill }} />
  );
}

// Depth-based spacer widths inserted between the arrow and icon to indent
// content while keeping the arrow at the leftmost position.
// depth 1: one arrow-width (1.75rem), depth 2: two arrow-widths (3.5rem)
const DEPTH_SPACER: Record<number, string> = {
  1: "1.75rem",
  2: "3.5rem",
};

interface StepRowProps {
  arrow?: ReactNode;
  icon?: ReactNode;
  stepType?: Type;
  label: ReactNode;
  labelClassName?: string;
  script?: ReactNode;
  count?: ReactNode;
  meta?: ReactNode;
  duration?: ReactNode | null;
  className?: string;
  onClick?: () => void;
  depth?: number;
}

export function StepRow({
  arrow,
  icon,
  stepType,
  label,
  labelClassName,
  script,
  count,
  meta,
  duration,
  className,
  onClick,
  depth = 0,
}: StepRowProps) {
  return (
    <div
      className={twMerge("flex w-full items-center", className)}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? -1 : undefined}
    >
      {/* Zone 1: Fixed — arrow + spacer + icon */}
      <div className="flex w-7 shrink-0 items-center justify-center">{arrow}</div>
      {DEPTH_SPACER[depth] && <div className="shrink-0" style={{ width: DEPTH_SPACER[depth] }} />}
      <div className="flex w-6 shrink-0 items-center justify-center pl-px">{icon}</div>
      {stepType && stepType !== Type.Group && <StepTypeBadge stepType={stepType} script={script} />}

      {/* Zone 2: Flexible — label + count */}
      <div className="ml-1.5 flex min-w-0 flex-auto items-center gap-2 self-stretch overflow-hidden py-1 text-left">
        <div
          className={twMerge(
            "text-charcoal-700 max-w-full shrink-0 truncate font-medium group-hover/link:opacity-100 group-aria-[current=step]/link:opacity-100 group-data-[step-state='waiting_for_dependencies']/link:opacity-45 group-data-[step-state=ignored]/link:opacity-45",
            labelClassName,
          )}
        >
          {label}
        </div>
        {count && <span className="shrink-0">{count}</span>}
      </div>

      {/* Zone 3: Right-aligned — meta + duration */}
      {(meta || duration !== undefined) && (
        <div className="container-hidden @xs/list:inline-flex ml-auto shrink-0 items-center gap-4 self-stretch pl-4 text-sm">
          {meta}
          {duration !== undefined && (
            <span className="text-charcoal-300 whitespace-nowrap text-right tabular-nums">
              {duration ?? "--"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
